import { useNavigate, useParams } from 'react-router-dom';
import { Form, message } from 'antd';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { TaskOrder, createSprint } from '../../api/workspace';
import { PlanningSessionStatus, PlanningSessionType, TimerConfiguration } from '../../types/PlanningSessionType';
import { connectToSocket, socket } from '../../api/socket';
import { decodeSocketPayload } from '../../hooks/socketPayload';
import { SocketEvent } from '../../types/realtime-contract';

import useManageTasks from '../TaskManagement/useManageTasks';
import TaskManagement from '../TaskManagement';
import { WorkspaceFramework } from '../../types/Workspace';
import { useGetTasks } from '../../serverState/tasks';
import './PlanningSession.scss';
import { useTimerStore } from './timerStore';
import { queryClient } from '../../queryClient';
import Task from '../../types/Task';
import { TaskStatus, defaultTaskStatuses } from '../../types/TaskStatus';
import { usePlanningSession } from '../../serverState/planning';
import { TaskTableDataType } from '../TaskTable/types/TaskTableTypes';
import { MoveSprintTaskToPlanningModal } from '../Modal/MoveSprintTaskToPlanningModal';
import { updateTask } from '../../api/task.api';
import { WorkspaceView, WorkspaceViewGroup } from '../../types/WorkspaceView';
import { useGetWorkspace } from '../../serverState/workspace';

export type TimerDrawerMode = 'collapsed' | 'expanded';

const PlanningSession = ({
  activeView,
  handleEditEstimationSettings,
}: {
  activeView: WorkspaceView;
  handleEditEstimationSettings: () => void;
}) => {
  const navigate = useNavigate();
  const workspaceQuery = useGetWorkspace();
  const spaceId = workspaceQuery.data?.id;

  const planningSessionQuery = usePlanningSession();
  const activeSprintId = workspaceQuery.data?.activeSprintId;

  const [allTasksInSpace, setAllTasksInSpace] = useState<TaskTableDataType[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [planningSession, setPlanningSession] = useState<PlanningSessionType>();
  const [messageApi, contextHolder] = message.useMessage();
  const [planningSessionId, setPlanningSessionId] = useState<number | null>(null);

  let taskStatuses: TaskStatus[] = defaultTaskStatuses;

  const [iterationPlanningDataSource, setIterationPlanningDataSource] = useState<TaskTableDataType[]>([]);

  const [showSprintToPlanningModal, setShowSprintToPlanningModal] = useState<boolean>();
  const [taskToMoveToPlanning, setTaskToMoveToPlanning] = useState<Task | null>(null);

  const setTimerConfig = useTimerStore((state) => state.setTimerConfig);

  const tasksQuery = useGetTasks(Number(spaceId));

  useEffect(() => {
    setAllTasksInSpace(tasksQuery.data);

    const tasksInPlanning = tasksQuery.data?.filter((t) => t.stage === 'planning') || [];

    const selectedRowKeysObject: { [id: number]: boolean } = {};
    tasksInPlanning.forEach((t) => {
      selectedRowKeysObject[t.key] = true;
    });
    setSelectedRowKeysObject(selectedRowKeysObject);
  }, [tasksQuery.data, planningSessionId]);

  useEffect(() => {
    taskStatuses = (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses;
    if (workspaceQuery.data?.framework === WorkspaceFramework.kanban) {
      navigate(`/space/${spaceId}/backlog`);
    }
  }, [workspaceQuery.data]);

  useEffect(() => {
    if (planningSessionQuery.data && spaceId) {
      setPlanningSessionId(planningSessionQuery.data.session.id);
      setPlanningSession(planningSessionQuery.data.session);

      if (planningSessionQuery.data.planningData) {
        // TODO: update to use timerStore
        setTimerConfig(planningSessionQuery.data.planningData);
      } else {
        // TODO: update to use timerStore
        setTimerConfig({
          duration: 10,
          createdAt: null,
          startedAt: null,
          status: 'paused',
          stoppedAt: null,
          totalSecondsPassed: null,
        });
      }

      // Removed on cleanup: the shared socket now stays connected after this view
      // unmounts, so a leftover handler would keep firing on every refetch's copy.
      const handlePlanningChanged = (raw: unknown) => {
        const { data } = decodeSocketPayload<TimerConfiguration>(raw);
        if (!data) return;
        // TODO: update to use timerStore
        setTimerConfig(data);
      };
      socket.on(SocketEvent.planningChanged, handlePlanningChanged);
      return () => {
        socket.off(SocketEvent.planningChanged, handlePlanningChanged);
      };
    }
  }, [planningSessionQuery.data]);

  // TODO: this is the only place selceted tasks state is handled
  const [selectedRowKeysObject, setSelectedRowKeysObject] = useState<{
    [id: number]: boolean;
  }>({});

  useEffect(() => {
    const selectedRowKeys: React.Key[] = [];
    Object.keys(selectedRowKeysObject).forEach((key) => {
      if (selectedRowKeysObject[Number(key)]) {
        selectedRowKeys.push(Number(key));
      }
    });
    addTasksToIterationPlanning(selectedRowKeys);
  }, [selectedRowKeysObject]);

  // No disconnect on cleanup: this is the app-wide socket. Disconnecting it here
  // dropped the user room, the space room and every listener's feed for the rest of
  // the page's life, so the Inbox badge (among others) froze until a refresh (DEV-3223).
  useEffect(() => {
    void establishSocketConnection();
  }, [planningSession]);

  const establishSocketConnection = async () => {
    if (planningSession && planningSession.status === PlanningSessionStatus.PLANNING_IN_PROGRESS) {
      await connectToSocket();
      // The shared socket is normally connected already, so 'connect' will not fire
      // again to send the join below. Send it directly in that case.
      if (socket.connected) socket.emit('planning', spaceId);
    }
  };

  useEffect(() => {
    const handleConnect = () => {
      socket.emit('planning', spaceId);
    };
    socket.on('connect', handleConnect);

    // Clean up the event handlers on component unmount
    return () => {
      socket.off('connect', handleConnect);
    };
  }, []);

  const addTasksToIterationPlanning = (selectedRowKeys: React.Key[]) => {
    const tasksToAdd: TaskTableDataType[] = [];

    // To implement adding and removing, we will every time replace the whole array
    if (selectedRowKeys.length === 0) {
      setIterationPlanningDataSource([]);
      return;
    } else {
      selectedRowKeys.forEach((key) => {
        const task = allTasksInSpace.filter((t) => t.key === key)[0];

        if (task) {
          tasksToAdd.push(task);
        }
        setIterationPlanningDataSource([...tasksToAdd]);
      });
    }
  };

  const addTaskToSelectedKeys = async (task: Task) => {
    if (task.sprintId && activeSprintId && task.sprintId === activeSprintId) {
      setTaskToMoveToPlanning(task);
      setShowSprintToPlanningModal(true);
    } else {
      const newSelectedRowKeysObject = { ...selectedRowKeysObject };
      newSelectedRowKeysObject[task.id] = true;
      setSelectedRowKeysObject(newSelectedRowKeysObject);
      await addTaskToPlanning(task);
    }
  };

  const removeTaskFromSelectedKeys = async (task: Task) => {
    const newSelectedRowKeysObject = { ...selectedRowKeysObject };
    delete newSelectedRowKeysObject[task.id];
    setSelectedRowKeysObject(newSelectedRowKeysObject);
    await removeTaskFromPlanning(task);
  };

  const addTaskToPlanning = async (task: Task) => {
    const updates = { planningSessionId: planningSessionId, sprintId: null, stage: 'planning' as const };
    if (spaceId) {
      queryClient.setQueryData(['tasks', Number(spaceId)], (old: TaskTableDataType[] | undefined) =>
        old ? old.map((t) => (t.id === task.id ? { ...t, ...updates } : t)) : old
      );
    }
    await updateTask(task.id, { ...task, ...updates });
  };

  const removeTaskFromPlanning = async (task: Task) => {
    // Planning membership is derived from stage === 'planning' (see effect above), so
    // clearing planningSessionId alone leaves the task in the list. Move it to backlog,
    // mirroring addTaskToPlanning's optimistic patch so the row leaves immediately.
    const updates = { planningSessionId: null, sprintId: null, stage: 'backlog' as const };
    if (spaceId) {
      queryClient.setQueryData(['tasks', Number(spaceId)], (old: TaskTableDataType[] | undefined) =>
        old ? old.map((t) => (t.id === task.id ? { ...t, ...updates } : t)) : old
      );
    }
    await updateTask(task.id, { ...task, ...updates });
  };

  const handleFinishPlanning = useCallback(async () => {
    try {
      if (iterationPlanningDataSource.length === 0) {
        void messageApi.error('Can not start sprint zero task!');
        return;
      }

      const tasksOrdering: TaskOrder[] = [];
      iterationPlanningDataSource.forEach((t) => {
        const foundIndex = tasksOrdering.findIndex((ord) => ord.statusId === t.statusId);
        if (foundIndex >= 0) {
          tasksOrdering[foundIndex].tasks.push(t.id);
        } else {
          tasksOrdering.push({ statusId: t.statusId, tasks: [t.id] });
        }
      });

      // LATER EDIT make sure a task ordering exists for every status
      workspaceQuery.data?.TaskStatus.forEach((status) => {
        if (!tasksOrdering.some((ord) => ord.statusId === status.id)) {
          tasksOrdering.push({ statusId: status.id, tasks: [] });
        }
      });

      if (spaceId && planningSessionId) {
        await createSprint(
          planningSession?.nextSprintName || 'Sprint',
          new Date(),
          undefined,
          +spaceId,
          +planningSessionId,
          iterationPlanningDataSource.map((at) => at.id),
          tasksOrdering
        );
        queryClient.invalidateQueries(['tasks', Number(spaceId)]);

        setTimeout(() => {
          const pathname = location.pathname.split('/');
          pathname.pop();
          pathname.push('sprint');
          navigate(pathname.join('/'));
        }, 100);
      } else {
        void messageApi.error('Missing data');
      }
    } catch (error: any) {
      void messageApi.error(error.message);
    }
  }, [spaceId, planningSession, iterationPlanningDataSource, allTasksInSpace]);

  // new Table implementation
  const tasksManager = useManageTasks(activeView);

  return (
    <>
      <MoveSprintTaskToPlanningModal
        open={showSprintToPlanningModal || false}
        onCancel={() => {
          if (taskToMoveToPlanning) {
            const newSelectedRowKeysObject = { ...selectedRowKeysObject };
            delete newSelectedRowKeysObject[taskToMoveToPlanning?.id];
            setSelectedRowKeysObject(newSelectedRowKeysObject);
          }
          setShowSprintToPlanningModal(false);
          setTaskToMoveToPlanning(null);
        }}
        onOk={() => {
          if (taskToMoveToPlanning) {
            addTaskToPlanning(taskToMoveToPlanning);
          }
          setTaskToMoveToPlanning(null);
          setShowSprintToPlanningModal(false);
        }}
        confirmLoading={false}
      />

      <handleFinishPlanningContext.Provider value={{ handleFinishPlanning }}>
        {/* Passes the flex fill through to the list, as the other modules do. Left as a
            plain block, the list had no height to fill and grew to its full content
            height, so every row rendered and the bottom space was unreachable. #2357 */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <TaskManagement
            {...tasksManager}
            planning={{
              dataSource: iterationPlanningDataSource,
              planningSession: planningSessionId || undefined,
              nextSprintName: planningSession?.nextSprintName || undefined,
              // TODO: check if this is even necessary anymore? ( the setDataSrouce)
              setTableDataSource: setIterationPlanningDataSource,
              rowSelection: {
                selectedRowKeys,
                onChange: (keys: React.Key[]) => {},
              },
              tableDataLoaded: false,
              usersWithAccessToSpace: tasksManager.usersWithAccessToSpace,

              customRowSelection: {
                selectedRowKeysObject,
                addTaskToSelectedKeys,
                removeTaskFromSelectedKeys,
              },
            }}
            activeView={activeView}
            handleEditEstimationSettings={handleEditEstimationSettings}
          />
        </div>
      </handleFinishPlanningContext.Provider>
    </>
  );
};

export default PlanningSession;

export const handleFinishPlanningContext = createContext<{
  handleFinishPlanning?: Function;
}>({});
